import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useCreateAsk } from '../../../api/asks';
import { useReferenceLists } from '../../../api/referenceLists';
import { SeedFormat, SeedTreatment } from '@spi/shared';
import type { CreateAskRequest } from '@spi/shared';

export function CreateAskForm({ projectId, onCreated }: { projectId: number; onCreated?: () => void }) {
  const createAsk = useCreateAsk(projectId);
  const { data: refLists } = useReferenceLists();
  const [showForm, setShowForm] = useState(false);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<CreateAskRequest>({
    defaultValues: {
      seedFormat: SeedFormat.BULK,
      seedTreatment: SeedTreatment.UNTREATED,
      acceptableTreatments: [],
      unacceptableTreatments: [],
    },
  });

  const seedFormat = watch('seedFormat');
  const seedTreatment = watch('seedTreatment');

  const onSubmit = async (data: CreateAskRequest) => {
    try {
      // Clean up NaN from empty number inputs
      if (data.latitude != null && isNaN(data.latitude)) data.latitude = undefined;
      if (data.longitude != null && isNaN(data.longitude)) data.longitude = undefined;
      await createAsk.mutateAsync(data);
      toast.success('Ask added');
      reset();
      setShowForm(false);
      onCreated?.();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to add ask');
    }
  };

  if (!showForm) {
    return (
      <button onClick={() => setShowForm(true)} className="btn btn-primary text-sm">
        Add Seed Request
      </button>
    );
  }

  return (
    <div className="card p-6">
      <h4 className="font-semibold mb-4">New Seed Request</h4>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="input-label">Crop *</label>
          <select {...register('crop', { required: 'Crop is required' })} className="input">
            <option value="">Select a crop</option>
            {refLists?.crop?.map((item) => (
              <option key={item.id} value={item.value}>{item.value}</option>
            ))}
          </select>
          {errors.crop && <p className="text-red-500 text-xs mt-1">{errors.crop.message}</p>}
        </div>

        <div>
          <label className="input-label">Preferred Varieties</label>
          <input {...register('preferredVarieties')} className="input" placeholder="e.g., NERICA 4, Kigega" />
        </div>

        <div>
          <label className="input-label">Seed Format *</label>
          <div className="flex gap-4">
            {Object.values(SeedFormat).map((format) => (
              <label key={format} className="flex items-center gap-2">
                <input type="radio" {...register('seedFormat')} value={format} className="text-spi-green" />
                <span className="text-sm capitalize">{format}</span>
              </label>
            ))}
          </div>
        </div>

        {(seedFormat === SeedFormat.BULK || seedFormat === SeedFormat.BOTH) && (
          <div>
            <label className="input-label">Quantity (kg) *</label>
            <input type="number" step="0.1" {...register('quantityKg', { valueAsNumber: true })} className="input" />
          </div>
        )}

        {(seedFormat === SeedFormat.PACKET || seedFormat === SeedFormat.BOTH) && (
          <div>
            <label className="input-label">Quantity (packets) *</label>
            <input type="number" {...register('quantityPackets', { valueAsNumber: true })} className="input" />
          </div>
        )}

        <div>
          <label className="input-label">Seed Treatment *</label>
          <div className="flex gap-4">
            {Object.values(SeedTreatment).map((treatment) => (
              <label key={treatment} className="flex items-center gap-2">
                <input type="radio" {...register('seedTreatment')} value={treatment} className="text-spi-green" />
                <span className="text-sm capitalize">{treatment}</span>
              </label>
            ))}
          </div>
        </div>

        {seedTreatment === SeedTreatment.TREATED && refLists?.treatment && (
          <div>
            <label className="input-label">Acceptable Treatments</label>
            <div className="flex flex-wrap gap-2">
              {refLists.treatment.map((item) => (
                <label key={item.id} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" {...register('acceptableTreatments')} value={item.value} />
                  {item.value}
                </label>
              ))}
            </div>
          </div>
        )}

        {refLists?.treatment && (
          <div>
            <label className="input-label">Unacceptable Treatments</label>
            <div className="flex flex-wrap gap-2">
              {refLists.treatment.map((item) => (
                <label key={item.id} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" {...register('unacceptableTreatments')} value={item.value} />
                  {item.value}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Optional delivery address override */}
        <div className="border-t pt-4 mt-4">
          <label className="input-label text-spi-slate">Optional: Override delivery address for this seed request</label>
          <p className="text-xs text-spi-slate mb-2">Leave blank to use the project's delivery address.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-spi-slate">Delivery Address</label>
              <input {...register('deliveryAddress')} className="input" placeholder="e.g., 123 Farm Road, Kampala" />
            </div>
            <div>
              <label className="text-xs text-spi-slate">Delivery Country</label>
              <input {...register('deliveryCountry')} className="input" placeholder="e.g., Uganda" />
            </div>
          </div>
        </div>

        {/* Optional location */}
        <div className="border-t pt-4 mt-4">
          <label className="input-label text-spi-slate">Optional: Custom location for this seed request</label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-spi-slate">Latitude (-90 to 90)</label>
              <input type="number" step="any" min={-90} max={90} {...register('latitude', { valueAsNumber: true })} className="input" placeholder="e.g., 0.3476" />
            </div>
            <div>
              <label className="text-xs text-spi-slate">Longitude (-180 to 180)</label>
              <input type="number" step="any" min={-180} max={180} {...register('longitude', { valueAsNumber: true })} className="input" placeholder="e.g., 32.5825" />
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={() => { reset(); setShowForm(false); }} className="btn btn-outline flex-1">
            Cancel
          </button>
          <button type="submit" disabled={createAsk.isPending} className="btn btn-primary flex-1">
            {createAsk.isPending ? 'Adding...' : 'Add Ask'}
          </button>
        </div>
      </form>
    </div>
  );
}
